import { useCallback, useEffect, useRef, useState } from "react";
import type { Player, TimeRange } from "@backstory/player";
import { DEFAULT_HALF_MS, KEY_STEP_MS, formatRange } from "@backstory/player";
import { formatClock } from "../../../lib/format";

/**
 * Range selection UI bound to the Player's SelectionModel. The bar mirrors the
 * scrubber: drag to select, click a handle to drag an edge, buttons set edges
 * at the playhead, Shift+←/→ extend the end (when the control has focus).
 */
export function RangeControl({ player, time }: { player: Player | null; time: number }) {
  const [range, setRange] = useState<TimeRange | null>(() => player?.selection.get() ?? null);
  const barRef = useRef<HTMLDivElement>(null);
  const duration = player?.duration ?? 0;

  useEffect(() => {
    if (!player) return;
    setRange(player.selection.get());
    return player.on("selectionchange", ({ range }) => setRange(range));
  }, [player]);

  const timeAt = useCallback(
    (clientX: number) => {
      const el = barRef.current;
      if (!el || !duration) return 0;
      const r = el.getBoundingClientRect();
      const frac = r.width ? Math.max(0, Math.min(1, (clientX - r.left) / r.width)) : 0;
      return frac * duration;
    },
    [duration],
  );

  const onPointerDown = (e: React.PointerEvent) => {
    if (!player) return;
    const target = e.target as HTMLElement;
    const edge = target.dataset.edge as "start" | "end" | undefined;
    e.preventDefault();
    const sel = player.selection;
    if (edge) {
      const move = (ev: PointerEvent) => (edge === "start" ? sel.setStart(timeAt(ev.clientX)) : sel.setEnd(timeAt(ev.clientX)));
      const up = () => {
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
      };
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
      return;
    }
    if (e.shiftKey) {
      sel.shiftClick(timeAt(e.clientX));
      return;
    }
    sel.beginDrag(timeAt(e.clientX));
    const move = (ev: PointerEvent) => sel.drag(timeAt(ev.clientX));
    const up = () => {
      sel.endDrag();
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!player) return;
    if (e.shiftKey && (e.key === "ArrowRight" || e.key === "ArrowLeft")) {
      e.preventDefault();
      player.selection.extend("end", e.key === "ArrowRight" ? KEY_STEP_MS : -KEY_STEP_MS, time);
    } else if (e.key === "Escape") player.selection.clear();
  };

  const pct = (ms: number) => (duration ? `${Math.max(0, Math.min(100, (ms / duration) * 100))}%` : "0%");
  const active = !!range && range.toMs > range.fromMs;

  return (
    <div className="range-control" data-testid="range-control">
      <div
        ref={barRef}
        className="range-bar"
        role="slider"
        tabIndex={0}
        aria-label="Time range for analysis"
        aria-valuemin={0}
        aria-valuemax={Math.round(duration)}
        aria-valuenow={range ? Math.round(range.fromMs) : Math.round(time)}
        aria-valuetext={active ? formatRange(range!, formatClock) : "no selection"}
        onPointerDown={onPointerDown}
        onKeyDown={onKeyDown}
        title="Drag to select · Shift+click to set anchors · Shift+←/→ to extend"
      >
        <div className="head" style={{ left: pct(time) }} aria-hidden="true" />
        {active && (
          <>
            <div className="sel" style={{ left: pct(range!.fromMs), width: `calc(${pct(range!.toMs)} - ${pct(range!.fromMs)})` }} />
            <div className="handle" data-edge="start" style={{ left: pct(range!.fromMs) }} aria-hidden="true" />
            <div className="handle" data-edge="end" style={{ left: pct(range!.toMs) }} aria-hidden="true" />
          </>
        )}
      </div>
      <div className="row">
        <span className="mono" data-testid="range-label">{active ? formatRange(range!, formatClock) : "No range selected"}</span>
        <span className="spacer" />
        <button type="button" className="btn ghost" onClick={() => player?.selection.setAround(time, DEFAULT_HALF_MS)} disabled={!player} title="±15 s around the playhead">±15 s here</button>
        <button type="button" className="btn ghost" onClick={() => player?.selection.setStart(time)} disabled={!player}>Start here</button>
        <button type="button" className="btn ghost" onClick={() => player?.selection.setEnd(time)} disabled={!player}>End here</button>
        {active && <button type="button" className="btn ghost" onClick={() => player?.selection.clear()}>Clear</button>}
      </div>
    </div>
  );
}
